/**
 * Events → Funnel ("משפך הגיוס") — the first end-to-end module view.
 *
 * Composes the live `/v1/contract/funnel` envelope with the honesty
 * primitives (`<Freshness/>`, `<Denominator/>`, `<AnomalyChip/>`) and
 * the prototype's Sankey funnel chart.
 */
import { useState } from 'react';
import type { Suite, ModuleSpec } from '../../../data/types/platform';
import { useEventsFunnel } from '../../../data/queries/useEventsFunnel';
import { Funnel } from '../../../components/charts/Funnel';
import { AnomalyChip, Denominator, Freshness } from '../../../components/honesty';
import { Icon } from '../../../components/ui/Icon';
import { formatNumber, formatPercent } from '../../../utils/format';

export interface FunnelViewProps {
  system?: Suite;
  module?: ModuleSpec;
}

function toEpoch(iso: string): number {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : Date.now();
}

export function FunnelView({ system, module }: FunnelViewProps) {
  const { data, isLoading, error } = useEventsFunnel();

  return (
    <article className="module-view funnel-view module-page">
      <header className="mp-head">
        <div className="mp-eyebrow">
          <span>{system?.en ?? 'EVENTS'}</span>
          <span className="mp-sep">/</span>
          <span>{module?.he ?? 'משפך הגיוס'}</span>
        </div>
        <h1>{module?.he ?? 'משפך הגיוס'}</h1>
        <p className="mp-sub">
          {module?.sub ?? 'הרשמה ← אישור ← הגעה — כולל walk-in שנכנס מהצד.'}
        </p>
        <div className="mp-meta">
          {data && (
            <>
              <Freshness updatedAt={toEpoch(data.source_pulled_at)} />
              <span className="mp-dot" />
              <span>scope: {data.scope}</span>
              {data.meta.mock && (
                <>
                  <span className="mp-dot" />
                  <span>mock fixtures</span>
                </>
              )}
            </>
          )}
        </div>
      </header>

      <div className="mp-body">
        {isLoading && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="loader" size={20} />
                <span>טוען נתוני משפך…</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="alert-triangle" size={20} />
                <span>שגיאה בטעינת המודול: {error.message}</span>
              </div>
            </div>
          </div>
        )}

        {data && <FunnelBody envelope={data} />}
      </div>
    </article>
  );
}

interface FunnelBodyProps {
  envelope: NonNullable<ReturnType<typeof useEventsFunnel>['data']>;
}

function FunnelBody({ envelope }: FunnelBodyProps) {
  const { totals } = envelope.data;
  const districts = envelope.data.by_district ?? [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const selectedRow = selectedKey
    ? districts.find((d) => rowKey(d) === selectedKey) ?? null
    : null;
  const scope = selectedRow
    ? {
        label: selectedRow.he ?? selectedRow.district ?? selectedRow.id ?? '—',
        reached: selectedRow.reached,
        rsvp_yes: selectedRow.rsvp_yes,
        attended: selectedRow.attended,
        walk_in: selectedRow.walk_in,
      }
    : {
        label: 'ארצי',
        reached: totals.reached,
        rsvp_yes: totals.rsvp_yes,
        attended: totals.attended,
        walk_in: totals.walk_in,
      };

  const yesAtt = Math.max(0, scope.attended - scope.walk_in);
  const yesShow = scope.rsvp_yes > 0 ? yesAtt / scope.rsvp_yes : 0;
  const reachToYes = scope.reached > 0 ? scope.rsvp_yes / scope.reached : 0;
  const walkInShare = scope.attended > 0 ? scope.walk_in / scope.attended : 0;
  const attendanceRecorded = Math.round(totals.records * envelope.recorded.attendance);

  return (
    <>
      {/* KPI row — mirrors the prototype's four-up layout, scoped to the
          currently-selected district (or national totals). */}
      <div className="kpi-grid cols-4">
        <KpiCell label="פניות ורישומים" value={formatNumber(scope.reached)} sub={`סך פניות תקפות · ${scope.label}`} />
        <KpiCell
          label="אישרו הגעה"
          value={formatNumber(scope.rsvp_yes)}
          sub={`${Math.round(reachToYes * 100)}% מהפניות`}
        />
        <KpiCell
          label="הגיעו"
          value={formatNumber(scope.attended)}
          sub={`${formatNumber(yesAtt)} אישרו מראש · ${formatNumber(scope.walk_in)} walk-in`}
          accent
        />
        <KpiCell
          label="המרת אישור←הגעה"
          value={`${Math.round(yesShow * 100)}%`}
          sub={`על בסיס ${formatPercent(envelope.recorded.attendance, 0)} רישום הגעה`}
          focus
        />
      </div>

      {/* Main funnel panel — re-renders when a district is selected. */}
      <div className="panel">
        <div className="panel-head" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="title">משפך {scope.label}</div>
          <div className="sub" style={{ flex: 1 }}>walk-in נכנס מהצד, ולא מאישורי ההגעה</div>
          {selectedRow && (
            <button
              type="button"
              onClick={() => setSelectedKey(null)}
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                padding: '6px 12px',
                background: 'var(--surface)',
                color: 'var(--ink)',
                border: '1px solid var(--hairline)',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Icon name="arrow-left" size={12} />
              חזרה למבט ארצי
            </button>
          )}
        </div>
        <Funnel
          reached={scope.reached}
          rsvp_yes={scope.rsvp_yes}
          attended={scope.attended}
          walk_in={scope.walk_in}
        />
      </div>

      {/* Per-district table — only if the API returned it. */}
      {districts.length > 0 && (
        <DistrictFunnelTable
          rows={districts}
          selectedKey={selectedKey}
          onSelect={(k) => setSelectedKey((prev) => (prev === k ? null : k))}
        />
      )}

      {/* Honesty footer: denominator + walk-in callout + anomalies. */}
      <footer
        className="mv-foot"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          padding: '16px 0 4px',
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          color: 'var(--ink-3)',
          alignItems: 'center',
        }}
      >
        <Denominator n={totals.records} total={totals.records} label="רשומות השתתפות" />
        <Denominator
          n={attendanceRecorded}
          total={totals.records}
          label="עם רישום הגעה"
        />
        <span style={{ color: 'var(--ink-3)' }}>
          walk-in: {Math.round(walkInShare * 100)}% מהמגיעים
        </span>
        {envelope.meta.anomalies.map((a) => (
          <AnomalyChip
            key={a.key}
            severity={a.severity === 'critical' ? 'alert' : a.severity}
            reason={a.message}
          />
        ))}
      </footer>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Local presentational helpers — match the prototype's KpiCell.      */
/* ------------------------------------------------------------------ */

interface KpiCellProps {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  focus?: boolean;
}

function KpiCell({ label, value, sub, accent, focus }: KpiCellProps) {
  const cls = ['kpi-cell', accent && 'accent', focus && 'focus']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-meta">{sub}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* District table — only rendered when by_district is present.        */
/* ------------------------------------------------------------------ */

type DistrictRow = NonNullable<
  NonNullable<ReturnType<typeof useEventsFunnel>['data']>['data']['by_district']
>[number];

function rowKey(r: DistrictRow): string {
  return r.id ?? r.district ?? r.he ?? '—';
}

interface DistrictFunnelTableProps {
  rows: DistrictRow[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
}

function DistrictFunnelTable({ rows, selectedKey, onSelect }: DistrictFunnelTableProps) {
  const maxReached = Math.max(1, ...rows.map((r) => r.reached));
  const sorted = [...rows].sort((a, b) => b.attended - a.attended);
  const arrowCell: React.CSSProperties = {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--ink-3)',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  };
  // Right-align all numeric headers so they line up with the .num-cell data
  // below them. In RTL the prototype's `text-align: left` puts headers and
  // data on opposite physical edges of their cells.
  const numHeader: React.CSSProperties = { textAlign: 'right' };
  return (
    <div className="panel">
      <div className="panel-head">
        <div className="title">משפך לפי מחוז</div>
        <div className="sub">לחץ על שורה כדי לעדכן את המשפך למעלה</div>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>מחוז</th>
            <th style={numHeader}>פניות</th>
            <th style={arrowCell}>← המרה</th>
            <th style={numHeader}>אישרו הגעה</th>
            <th style={arrowCell}>← המרה</th>
            <th style={numHeader}>הגיעו</th>
            <th style={numHeader}>walk-in</th>
            <th style={numHeader}>אישור←הגעה</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const yesAtt = Math.max(0, r.attended - r.walk_in);
            const yesShow = r.rsvp_yes > 0 ? yesAtt / r.rsvp_yes : 0;
            const reachToYes = r.reached > 0 ? r.rsvp_yes / r.reached : 0;
            const reachShare = r.reached / maxReached;
            const name = r.he ?? r.district ?? r.id ?? '—';
            const key = rowKey(r);
            const isSelected = selectedKey === key;
            return (
              <tr
                key={key + ':' + i}
                onClick={() => onSelect(key)}
                style={{
                  cursor: 'pointer',
                  background: isSelected ? 'var(--surface-2, #f3f1ec)' : undefined,
                  outline: isSelected ? '1px solid var(--ink)' : undefined,
                  outlineOffset: -1,
                }}
              >
                <td><b>{name}</b></td>
                <td className="num-cell">
                  <div className="cellbar">
                    <div className="track">
                      <div className="fill" style={{ width: `${reachShare * 100}%` }} />
                    </div>
                    <div className="v">{formatNumber(r.reached)}</div>
                  </div>
                </td>
                <td className="num-cell" style={arrowCell}>
                  {r.reached > 0 ? `${Math.round(reachToYes * 100)}%` : '—'}
                </td>
                <td className="num-cell">{formatNumber(r.rsvp_yes)}</td>
                <td className="num-cell" style={arrowCell}>
                  {r.rsvp_yes > 0 ? `${Math.round(yesShow * 100)}%` : '—'}
                </td>
                <td className="num-cell">{formatNumber(r.attended)}</td>
                <td className="num-cell">{formatNumber(r.walk_in)}</td>
                <td className="num-cell">{Math.round(yesShow * 100)}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
